/**
 * Tiny media-query subscription hook.
 *
 * Implemented on top of `useSyncExternalStore` — the React-recommended
 * primitive for wrapping browser APIs that emit change events. One
 * `MediaQueryList` is created per query (memoised), so the subscription
 * and the snapshot read the same list. The app is client-rendered only
 * (`createRoot`), so no server snapshot is needed.
 *
 * Usage:
 *   const isMobile = useMediaQuery('(max-width: 767px)');
 *   if (isMobile) return <Cards />;
 *   return <Table />;
 *
 * Common breakpoints (matched to Tailwind defaults — keep in sync if
 * we ever override the theme):
 *   sm: 640
 *   md: 768   ← table → cards switchover
 *   lg: 1024
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';

export function useMediaQuery(query: string): boolean {
  const mql = useMemo(() => window.matchMedia(query), [query]);
  // `subscribe` must be stable per list, otherwise useSyncExternalStore
  // re-subscribes on every render.
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    [mql],
  );
  return useSyncExternalStore(subscribe, () => mql.matches);
}

/** Tailwind `md` breakpoint — anything below is treated as a phone-sized layout. */
export const MEDIA_BELOW_MD = '(max-width: 767px)';
